import type {
  DriveLinkContext,
  DriveRole,
  DriveStatus,
} from '@/components/drives/drive-ui-state';
import type { SupportedLocale } from '@/i18n/config';

export type DriveLocalizedLabels = {
  roleLabels: Record<DriveRole, string>;
  statusLabels: Record<DriveStatus, string>;
  contextLabels: Record<DriveLinkContext, string>;
  activityEvent: string;
  softAllocation: string;
  hardCap: string;
  settingsUnavailableForStatus: string;
  driveFallbackDescription: string;
  loadMoreFolders: string;
  loadMoreFiles: string;
  guestStatusActive: string;
  guestStatusReviewRequired: string;
  publishCompleted: string;
};

const LABELS: Record<SupportedLocale, DriveLocalizedLabels> = {
  en: {
    roleLabels: {
      viewer: 'Viewer',
      commenter: 'Commenter',
      contributor: 'Contributor',
      content_manager: 'Content Manager',
      manager: 'Manager',
    },
    statusLabels: {
      migrating: 'Migrating',
      active: 'Active',
      read_only: 'Read-only',
      archived: 'Archived',
      trashed: 'In trash',
      recovery: 'Recovery',
    },
    contextLabels: { team: 'Team', project: 'Project' },
    activityEvent: 'Drive activity',
    softAllocation: 'Soft allocation',
    hardCap: 'Hard cap',
    settingsUnavailableForStatus:
      'Settings changes are unavailable while this Drive is not active.',
    driveFallbackDescription: 'Shared workspace files.',
    loadMoreFolders: 'Load more folders',
    loadMoreFiles: 'Load more files',
    guestStatusActive: 'Active',
    guestStatusReviewRequired: 'Review required',
    publishCompleted: 'Published to the workspace Drive.',
  },
  'pt-BR': {
    roleLabels: {
      viewer: 'Visualizador',
      commenter: 'Coment\u00E1rio',
      contributor: 'Contribuidor',
      content_manager: 'Gerente de Conte\u00FAdo',
      manager: 'Gerente.',
    },
    statusLabels: {
      migrating: 'Migrando.',
      active: 'Ativo.',
      read_only: 'Apenas leitura',
      archived: 'Arquivado',
      trashed: 'No lixo',
      recovery: 'Recupera\u00E7\u00E3o',
    },
    contextLabels: { team: 'Equipe', project: 'Projeto' },
    activityEvent: 'Atividade de condu\u00E7\u00E3o.',
    softAllocation: 'Aloca\u00E7\u00E3o suave.',
    hardCap: 'Capa dura.',
    settingsUnavailableForStatus:
      'As mudan\u00E7as de configura\u00E7\u00E3o n\u00E3o est\u00E3o dispon\u00EDveis enquanto esta unidade n\u00E3o est\u00E1 ativa.',
    driveFallbackDescription: 'Arquivos compartilhados.',
    loadMoreFolders: 'Carregue mais pastas.',
    loadMoreFiles: 'Carregue mais arquivos.',
    guestStatusActive: 'Ativo.',
    guestStatusReviewRequired: 'Revis\u00E3o necess\u00E1ria.',
    publishCompleted: 'Publicado na unidade de trabalho.',
  },
  ar: {
    roleLabels: {
      viewer: 'مشاهد',
      commenter: 'معلّق',
      contributor: 'مساهم',
      content_manager: 'مدير المحتوى',
      manager: 'مدير',
    },
    statusLabels: {
      migrating: 'جارٍ الترحيل',
      active: 'نشط',
      read_only: 'للقراءة فقط',
      archived: 'مؤرشف',
      trashed: 'في المهملات',
      recovery: 'استرداد',
    },
    contextLabels: { team: 'فريق', project: 'مشروع' },
    activityEvent: 'نشاط محرك الأقراص',
    softAllocation: 'السعة المرنة',
    hardCap: 'الحد الأقصى',
    settingsUnavailableForStatus:
      'لا يمكن تغيير الإعدادات عندما لا يكون محرك الأقراص نشطًا.',
    driveFallbackDescription: 'تخزين مشترك ومحكوم لمساحة العمل.',
    loadMoreFolders: 'تحميل المزيد من المجلدات',
    loadMoreFiles: 'تحميل المزيد من الملفات',
    guestStatusActive: 'نشط',
    guestStatusReviewRequired: 'المراجعة مطلوبة',
    publishCompleted: 'تم النشر في محرك مساحة العمل.',
  },
  de: {
    roleLabels: {
      viewer: 'Betrachter',
      commenter: 'Kommentator',
      contributor: 'Mitwirkender',
      content_manager: 'Inhaltsmanager',
      manager: 'Manager',
    },
    statusLabels: {
      migrating: 'Migration läuft',
      active: 'Aktiv',
      read_only: 'Schreibgeschützt',
      archived: 'Archiviert',
      trashed: 'Im Papierkorb',
      recovery: 'Wiederherstellung',
    },
    contextLabels: { team: 'Team', project: 'Projekt' },
    activityEvent: 'Ablagenaktivität',
    softAllocation: 'Weiches Kontingent',
    hardCap: 'Harte Obergrenze',
    settingsUnavailableForStatus:
      'Einstellungen können nur für aktive Ablagen geändert werden.',
    driveFallbackDescription: 'Geteilter, verwalteter Workspace-Speicher.',
    loadMoreFolders: 'Weitere Ordner laden',
    loadMoreFiles: 'Weitere Dateien laden',
    guestStatusActive: 'Aktiv',
    guestStatusReviewRequired: 'Prüfung erforderlich',
    publishCompleted: 'In der Workspace-Ablage veröffentlicht.',
  },
  es: {
    roleLabels: {
      viewer: 'Lector',
      commenter: 'Comentarista',
      contributor: 'Colaborador',
      content_manager: 'Gestor de contenido',
      manager: 'Administrador',
    },
    statusLabels: {
      migrating: 'Migrando',
      active: 'Activa',
      read_only: 'Solo lectura',
      archived: 'Archivada',
      trashed: 'En la papelera',
      recovery: 'Recuperación',
    },
    contextLabels: { team: 'Equipo', project: 'Proyecto' },
    activityEvent: 'Actividad de la unidad',
    softAllocation: 'Asignación flexible',
    hardCap: 'Límite máximo',
    settingsUnavailableForStatus:
      'La configuración solo se puede cambiar cuando la unidad está activa.',
    driveFallbackDescription:
      'Almacenamiento compartido y administrado del espacio.',
    loadMoreFolders: 'Cargar más carpetas',
    loadMoreFiles: 'Cargar más archivos',
    guestStatusActive: 'Activo',
    guestStatusReviewRequired: 'Revisión necesaria',
    publishCompleted: 'Publicado en la unidad del espacio.',
  },
  fr: {
    roleLabels: {
      viewer: 'Lecteur',
      commenter: 'Commentateur',
      contributor: 'Contributeur',
      content_manager: 'Gestionnaire de contenu',
      manager: 'Gestionnaire',
    },
    statusLabels: {
      migrating: 'Migration en cours',
      active: 'Actif',
      read_only: 'Lecture seule',
      archived: 'Archivé',
      trashed: 'Dans la corbeille',
      recovery: 'Récupération',
    },
    contextLabels: { team: 'Équipe', project: 'Projet' },
    activityEvent: 'Activité de l’espace',
    softAllocation: 'Allocation souple',
    hardCap: 'Limite stricte',
    settingsUnavailableForStatus:
      'Les réglages ne peuvent être modifiés que lorsque cet espace est actif.',
    driveFallbackDescription:
      'Stockage partagé et gouverné de l’espace de travail.',
    loadMoreFolders: 'Charger plus de dossiers',
    loadMoreFiles: 'Charger plus de fichiers',
    guestStatusActive: 'Actif',
    guestStatusReviewRequired: 'Révision requise',
    publishCompleted: 'Publié dans l’espace du workspace.',
  },
  hi: {
    roleLabels: {
      viewer: 'दर्शक',
      commenter: 'टिप्पणीकार',
      contributor: 'योगदानकर्ता',
      content_manager: 'कंटेंट मैनेजर',
      manager: 'मैनेजर',
    },
    statusLabels: {
      migrating: 'माइग्रेट हो रहा है',
      active: 'सक्रिय',
      read_only: 'केवल पढ़ने योग्य',
      archived: 'आर्काइव किया गया',
      trashed: 'ट्रैश में',
      recovery: 'रिकवरी',
    },
    contextLabels: { team: 'टीम', project: 'प्रोजेक्ट' },
    activityEvent: 'ड्राइव गतिविधि',
    softAllocation: 'सॉफ्ट आवंटन',
    hardCap: 'अधिकतम सीमा',
    settingsUnavailableForStatus:
      'ड्राइव सक्रिय न होने पर सेटिंग बदली नहीं जा सकती।',
    driveFallbackDescription: 'साझा और नियंत्रित वर्कस्पेस स्टोरेज।',
    loadMoreFolders: 'और फ़ोल्डर लोड करें',
    loadMoreFiles: 'और फ़ाइलें लोड करें',
    guestStatusActive: 'सक्रिय',
    guestStatusReviewRequired: 'समीक्षा आवश्यक',
    publishCompleted: 'वर्कस्पेस ड्राइव में प्रकाशित किया गया।',
  },
  id: {
    roleLabels: {
      viewer: 'Pelihat',
      commenter: 'Pemberi komentar',
      contributor: 'Kontributor',
      content_manager: 'Pengelola konten',
      manager: 'Pengelola',
    },
    statusLabels: {
      migrating: 'Sedang dimigrasikan',
      active: 'Aktif',
      read_only: 'Hanya baca',
      archived: 'Diarsipkan',
      trashed: 'Di sampah',
      recovery: 'Pemulihan',
    },
    contextLabels: { team: 'Tim', project: 'Proyek' },
    activityEvent: 'Aktivitas Drive',
    softAllocation: 'Alokasi lunak',
    hardCap: 'Batas maksimum',
    settingsUnavailableForStatus:
      'Pengaturan hanya dapat diubah saat Drive aktif.',
    driveFallbackDescription: 'Penyimpanan workspace bersama yang terkelola.',
    loadMoreFolders: 'Muat folder lainnya',
    loadMoreFiles: 'Muat file lainnya',
    guestStatusActive: 'Aktif',
    guestStatusReviewRequired: 'Perlu ditinjau',
    publishCompleted: 'Diterbitkan ke Drive Workspace.',
  },
  it: {
    roleLabels: {
      viewer: 'Visualizzatore',
      commenter: 'Commentatore',
      contributor: 'Collaboratore',
      content_manager: 'Gestore contenuti',
      manager: 'Gestore',
    },
    statusLabels: {
      migrating: 'Migrazione in corso',
      active: 'Attivo',
      read_only: 'Sola lettura',
      archived: 'Archiviato',
      trashed: 'Nel cestino',
      recovery: 'Recupero',
    },
    contextLabels: { team: 'Team', project: 'Progetto' },
    activityEvent: 'Attività del Drive',
    softAllocation: 'Allocazione flessibile',
    hardCap: 'Limite massimo',
    settingsUnavailableForStatus:
      'Le impostazioni si possono modificare solo quando il Drive è attivo.',
    driveFallbackDescription: 'Spazio di lavoro condiviso e gestito.',
    loadMoreFolders: 'Carica altre cartelle',
    loadMoreFiles: 'Carica altri file',
    guestStatusActive: 'Attivo',
    guestStatusReviewRequired: 'Revisione richiesta',
    publishCompleted: 'Pubblicato nel Drive dello spazio.',
  },
  ja: {
    roleLabels: {
      viewer: '閲覧者',
      commenter: 'コメント投稿者',
      contributor: '投稿者',
      content_manager: 'コンテンツ管理者',
      manager: '管理者',
    },
    statusLabels: {
      migrating: '移行中',
      active: '有効',
      read_only: '読み取り専用',
      archived: 'アーカイブ済み',
      trashed: 'ゴミ箱',
      recovery: '復旧',
    },
    contextLabels: { team: 'チーム', project: 'プロジェクト' },
    activityEvent: 'ドライブのアクティビティ',
    softAllocation: 'ソフト割り当て',
    hardCap: '上限',
    settingsUnavailableForStatus:
      'ドライブが有効な場合にのみ設定を変更できます。',
    driveFallbackDescription: '共有され、管理されたワークスペース ストレージ。',
    loadMoreFolders: 'フォルダをさらに読み込む',
    loadMoreFiles: 'ファイルをさらに読み込む',
    guestStatusActive: '有効',
    guestStatusReviewRequired: 'レビューが必要',
    publishCompleted: 'ワークスペース ドライブに公開しました。',
  },
  ko: {
    roleLabels: {
      viewer: '뷰어',
      commenter: '댓글 작성자',
      contributor: '기여자',
      content_manager: '콘텐츠 관리자',
      manager: '관리자',
    },
    statusLabels: {
      migrating: '이전 중',
      active: '활성',
      read_only: '읽기 전용',
      archived: '보관됨',
      trashed: '휴지통',
      recovery: '복구',
    },
    contextLabels: { team: '팀', project: '프로젝트' },
    activityEvent: '드라이브 활동',
    softAllocation: '권장 할당량',
    hardCap: '최대 한도',
    settingsUnavailableForStatus:
      '드라이브가 활성 상태일 때만 설정을 변경할 수 있습니다.',
    driveFallbackDescription: '공유되고 관리되는 워크스페이스 저장소입니다.',
    loadMoreFolders: '폴더 더 불러오기',
    loadMoreFiles: '파일 더 불러오기',
    guestStatusActive: '활성',
    guestStatusReviewRequired: '검토 필요',
    publishCompleted: '워크스페이스 드라이브에 게시했습니다.',
  },
  ru: {
    roleLabels: {
      viewer: 'Читатель',
      commenter: 'Комментатор',
      contributor: 'Участник',
      content_manager: 'Менеджер контента',
      manager: 'Менеджер',
    },
    statusLabels: {
      migrating: 'Выполняется перенос',
      active: 'Активен',
      read_only: 'Только чтение',
      archived: 'В архиве',
      trashed: 'В корзине',
      recovery: 'Восстановление',
    },
    contextLabels: { team: 'Команда', project: 'Проект' },
    activityEvent: 'Активность диска',
    softAllocation: 'Мягкая квота',
    hardCap: 'Жёсткий лимит',
    settingsUnavailableForStatus:
      'Настройки можно менять только для активного диска.',
    driveFallbackDescription: 'Общее управляемое хранилище пространства.',
    loadMoreFolders: 'Загрузить ещё папки',
    loadMoreFiles: 'Загрузить ещё файлы',
    guestStatusActive: 'Активен',
    guestStatusReviewRequired: 'Требуется проверка',
    publishCompleted: 'Опубликовано на диске пространства.',
  },
  vi: {
    roleLabels: {
      viewer: 'Người xem',
      commenter: 'Người bình luận',
      contributor: 'Cộng tác viên',
      content_manager: 'Quản lý nội dung',
      manager: 'Quản lý',
    },
    statusLabels: {
      migrating: 'Đang di chuyển',
      active: 'Đang hoạt động',
      read_only: 'Chỉ đọc',
      archived: 'Đã lưu trữ',
      trashed: 'Trong thùng rác',
      recovery: 'Khôi phục',
    },
    contextLabels: { team: 'Nhóm', project: 'Dự án' },
    activityEvent: 'Hoạt động của Drive',
    softAllocation: 'Hạn mức mềm',
    hardCap: 'Giới hạn cứng',
    settingsUnavailableForStatus:
      'Chỉ có thể thay đổi cài đặt khi Drive đang hoạt động.',
    driveFallbackDescription: 'Kho lưu trữ workspace dùng chung được quản trị.',
    loadMoreFolders: 'Tải thêm thư mục',
    loadMoreFiles: 'Tải thêm tệp',
    guestStatusActive: 'Đang hoạt động',
    guestStatusReviewRequired: 'Cần xem xét',
    publishCompleted: 'Đã xuất bản lên ổ đĩa workspace.',
  },
  zh: {
    roleLabels: {
      viewer: '查看者',
      commenter: '评论者',
      contributor: '贡献者',
      content_manager: '内容管理员',
      manager: '管理员',
    },
    statusLabels: {
      migrating: '正在迁移',
      active: '活跃',
      read_only: '只读',
      archived: '已归档',
      trashed: '在回收站中',
      recovery: '恢复',
    },
    contextLabels: { team: '团队', project: '项目' },
    activityEvent: '云端硬盘活动',
    softAllocation: '软配额',
    hardCap: '硬上限',
    settingsUnavailableForStatus: '仅可在云端硬盘处于活跃状态时更改设置。',
    driveFallbackDescription: '共享且受管控的工作区存储。',
    loadMoreFolders: '加载更多文件夹',
    loadMoreFiles: '加载更多文件',
    guestStatusActive: '活跃',
    guestStatusReviewRequired: '需要审核',
    publishCompleted: '已发布到工作区云端硬盘。',
  },
  'zh-TW': {
    roleLabels: {
      viewer: '檢視者',
      commenter: '留言者',
      contributor: '協作者',
      content_manager: '內容管理員',
      manager: '管理員',
    },
    statusLabels: {
      migrating: '正在移轉',
      active: '使用中',
      read_only: '唯讀',
      archived: '已封存',
      trashed: '在垃圾桶中',
      recovery: '復原',
    },
    contextLabels: { team: '團隊', project: '專案' },
    activityEvent: '雲端硬碟活動',
    softAllocation: '彈性配額',
    hardCap: '硬性上限',
    settingsUnavailableForStatus: '只有在雲端硬碟使用中時才能變更設定。',
    driveFallbackDescription: '共用且受管理的工作區儲存空間。',
    loadMoreFolders: '載入更多資料夾',
    loadMoreFiles: '載入更多檔案',
    guestStatusActive: '使用中',
    guestStatusReviewRequired: '需要審查',
    publishCompleted: '已發佈到工作區雲端硬碟。',
  },
};

export function getDriveLocalizedLabels(
  locale: SupportedLocale
): DriveLocalizedLabels {
  return LABELS[locale];
}
